<template>
  <!-- prettier-ignore -->
  <the-form-modal
    v-model="isSaveNudgeOpen"
    locked
    name="saveNudge"
  >
    <Modal.Header :text="$t('basket.saveNudge.saveForLater')" />

    <Form.Form
      v-unblu-protected
      class="screen-form form"
      name="save_nudge"
      tracking-disabled
      :validator="v"
      @cancel="close"
      @submit="submit"
    >
      <Modal.Content>
        <the-typography
          type="heading05"
          v-text="$t('basket.modal.basketScreenModalText')"
        />

        <Form.Input
          v-model="formData.email"
          class="form__input"
          name="email"
          required
          theme="dark"
          type="email"
          :placeholder="$t('person.email')"
          :title-label="$t('person.email')"
          :validate="v.email"
        />

        <the-typography
          type="heading05"
          v-text="$t('basket.modal.basketScreenDataProtection')"
        />
        <the-typography
          type="bodyLargeLong"
          v-text="$t('basket.modal.basketScreenDataProtectionText')"
        />
      </Modal.Content>

      <Modal.Footer>
        <template #primary>
          <Form.Button
            class="form__button"
            has-loading-indicator
            primary
            type="submit"
            :text="$t('form.save')"
          />
        </template>

        <template #secondary>
          <Form.Button
            class="form__button"
            secondary
            :text="$t('form.cancel')"
            @click="close"
          />
        </template>
      </Modal.Footer>
    </Form.Form>
  </the-form-modal>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import useVuelidate from '@vuelidate/core'

import { email } from '@/validations/components'
import { loading$, events$ } from '@/services'
import { ReactivityUtil } from '@/utils/Reactivity'

import basketStore from '@/store/basket'
import useI18n from '@/hooks/useI18n'
import useProductPersons from '@/hooks/useProductPersons'
import useToast from '@/hooks/useToast'

import * as Form from '@/components/Form'
import * as Modal from '@/components/Modal'

import { ROUTE_INDEX, SAVE_NUDGE_DELAY } from '@/config/constants'
import { EVENT_BASKET, EVENT_OVP, SOURCE } from '@/config/events'

// HOOKS
const { t } = useI18n()
const { hasSelectedProducts } = useProductPersons()
const route = useRoute()
const { addToast, removeToast } = useToast()

// DATA
const formData = reactive({
  email: undefined,
})
const isSaveNudgeOpen = ref(false)
const isSaveNudgeEnabled = parseInt(import.meta.env.VITE_FEATURE_SAVE_NUDGE) === 1
let nudgeInstance = false
const v = useVuelidate(
  {
    email,
  },
  formData,
  { $lazy: true }
)

// METHODS
function close(trackClose = true) {
  isSaveNudgeOpen.value = false

  if (nudgeInstance) removeToast(nudgeInstance)
  if (trackClose) events$.emit(EVENT_OVP.NUDGE_CLOSE)
}

function registerListener() {
  if (isSaveNudgeEnabled && !basketStore.basket.email && !hasSelectedProducts.value) {
    events$.once(EVENT_BASKET.WIDGET_CLOSE, showNudging)
  }
}

function showNudging() {
  if (isSaveNudgeEnabled && !basketStore.basket.email) {
    window.setTimeout(() => {
      // don't show on basket and later screens
      if (route.meta.index >= ROUTE_INDEX.BASKET) {
        return
      }

      nudgeInstance = addToast({
        actions: [
          {
            label: t('form.save'),
            callback: toastId => {
              removeToast(toastId)
              isSaveNudgeOpen.value = true
            },
          },
        ],
        isPermanent: true,
        text: t('basket.modal.basketSavingTitle'),
      })
    }, SAVE_NUDGE_DELAY)
  }
}

async function submit() {
  const offerUpdate = !!ReactivityUtil.clone(basketStore.basket.oaBasketId)

  loading$.start()

  const payload = Object.assign({}, formData)
  try {
    const __basket = await basketStore.updateOvpBasket(payload, {}, { emailType: 'OFFER_REENTRY_WITHOUT_EGATE' })

    close(false)
    events$.emit(EVENT_BASKET.SAVED, {
      basket: __basket,
      source: SOURCE.SAVE_NUDGE,
      update: offerUpdate,
    })

    addToast({
      text: t('basket.modal.basketSaved'),
    })
    loading$.end()
  } catch {
    loading$.failed()
  }
}

// WATCHERS
const stopWatching = watch(route, it => {
  if (it.meta.index >= ROUTE_INDEX.BASKET) {
    close(false)
    stopWatching()
  }
})

// LIFECYCLE HOOKS
onMounted(() => {
  registerListener()
})
</script>
